<template>
  <div class="mood-inverse bg-[#191c20] text-static-default-hi">
    <ErrorPage v-if="!category" />

    <template v-else>
      <div class="flex flex-col">
        <div ref="scrollContainerRef" class="flex-1">
          <OutletBreadcrumb :category-slug />
          <OutletReassuranceBanner v-if="isWebview" />
          <RevContainer v-else class="flex items-center pb-32">
            <TrustList />
          </RevContainer>

          <component
            :is="!isWebview ? RevContainer : 'div'"
            :class="{ 'px-16': isWebview }"
          >
            <h1
              v-if="!isWebview && category?.label"
              class="heading-1 lg:punchline pt-16"
            >
              {{ i18n(category.label) }}
            </h1>
            <OutletFilterBar
              v-model:filters="filtersInput"
              v-model:price="priceInput"
              :facets="data.facets"
              :loading="isInitialLoading"
              :placeholder-facets="category?.facets ?? []"
              :price-facet="data.priceFacet"
              :search-nb-results="data.nbHits"
            />
          </component>

          <component
            :is="!isWebview ? RevContainer : 'div'"
            :class="isWebview ? 'px-16 py-16' : 'py-16'"
          >
            <RevSkeleton
              v-if="isInitialLoading"
              height="20px"
              shape="rectangle"
              width="40%"
            />
            <div v-else class="flex items-center gap-8 justify-between">
              <p class="body-2 text-static-default-low">
                {{ i18n(translations.productCount, { count: data.nbHits }) }}
              </p>

              <OutletCounter
                v-if="category?.endDate"
                :date="new Date(category.endDate)"
                :prefix="i18n(translations.countdownPrefix)"
              />
            </div>
          </component>

          <component :is="!isWebview ? RevContainer : 'div'">
            <ProductGridSkeleton v-if="isInitialLoading" :columns />
            <SoldOutPage v-else-if="data.hits.length === 0" />
            <ProductGrid
              v-else
              :columns
              :get-device-type
              :get-product-images
              :products="data.hits"
            />
          </component>

          <RevPagination
            v-if="data.nbPages > 1"
            v-model:current-page="currentPage"
            class="py-24"
            :current-page-label="`${currentPage}`"
            :navigation-aria-label="i18n(translations.paginationLabel)"
            :next-aria-label="i18n(translations.paginationNext)"
            :page-aria-label="getPageAriaLabel"
            :page-count="data.nbPages"
            :previous-aria-label="i18n(translations.paginationPrevious)"
            @click="
              (payload) => {
                currentPage = payload.page
                scrollContainerRef?.scrollIntoView({
                  block: 'start',
                  behavior: 'smooth',
                })
              }
            "
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useHead, useRoute } from '#imports'
import { computed, reactive, ref, watch } from 'vue'

import { getInitialFiltersDict } from '@backmarket/nuxt-layer-recommendation/filtering/getInitialFiltersDict'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { RevContainer } from '@ds/components/Container'
import { RevPagination } from '@ds/components/Pagination'
import { RevSkeleton } from '@ds/components/Skeleton'

import TrustList from '~/scopes/reassurance/components/TrustList/TrustList.vue'

import ErrorPage from '../../components/ErrorPage/ErrorPage.vue'
import OutletBreadcrumb from '../../components/OutletBreadcrumb/OutletBreadcrumb.vue'
import OutletCounter from '../../components/OutletCounter/OutletCounter.vue'
import OutletReassuranceBanner from '../../components/OutletReassuranceBanner/OutletReassuranceBanner.vue'
import { useOutletFiltersFromUrl } from '../../composables/useOutletFiltersFromUrl'
import { useOutletSearch } from '../../composables/useOutletSearch'
import { useProductImages } from '../../composables/useProductImages/useProductImages'
import { OUTLET_LANDING_CARDS, type OutletCategorySlug } from '../../constants'

import translations from './OutletCategory.translations'
import OutletFilterBar from './components/OutletFilterBar/OutletFilterBar.vue'
import ProductGrid from './components/ProductGrid/ProductGrid.vue'
import ProductGridSkeleton from './components/ProductGridSkeleton/ProductGridSkeleton.vue'
import SoldOutPage from './components/SoldOutPage/SoldOutPage.vue'

const i18n = useI18n()
const route = useRoute()
const isWebview = useIsWebview()

function getPageAriaLabel(page: number) {
  return i18n(translations.paginationGoToPage, { page })
}

// Load product images once at page level
const { getProductImages, getDeviceType } = useProductImages()

// The slug in the URL must match one of the declared outlet categories.
// Anything else shows the ErrorPage.
const category = computed(() =>
  OUTLET_LANDING_CARDS.find((c) => c.slug === route.params.slug),
)

// Paint the body dark so any overscroll stays on-theme.
useHead({
  title: category.value?.label ? i18n(category.value.label) : '',
  bodyAttrs: { style: 'background-color: #191c20' },
})

const columns = computed(() => category.value?.defaultColumns ?? 2)

const categorySlug = computed(() => route.params.slug as OutletCategorySlug)
const currentPage = ref(1)
const scrollContainerRef = ref<HTMLElement>()

const filtersInput = reactive<Record<string, string[]>>(
  getInitialFiltersDict(category.value?.facets ?? []),
)
const priceInput = ref<[number, number] | undefined>()

// Reset pagination + every active filter when the user navigates to a
// different outlet category. The facet list differs per category, so the
// previous selections aren't meaningful anymore.
watch(categorySlug, () => {
  currentPage.value = 1
  priceInput.value = undefined
  for (const key of Object.keys(filtersInput)) delete filtersInput[key]
  const next = OUTLET_LANDING_CARDS.find((c) => c.slug === categorySlug.value)
  for (const facet of next?.facets ?? []) {
    filtersInput[facet.name] = []
  }
})

// Persist filters, price range and page in the URL (hash + ?p=) so that
// navigating to a PDP and coming back restores the exact view. The composable
// also resets the page to 1 when refinements change (replacing the previous
// page-level watcher that did the same).
useOutletFiltersFromUrl(filtersInput, priceInput, currentPage, categorySlug)

const { data, pending } = useOutletSearch({
  categorySlug,
  page: currentPage,
  filtersInput,
  priceInput,
})

// True only on the very first fetch (before any hits ever land). Subsequent
// filter/page changes keep the previous chips and count visible to avoid
// flicker — we only flip back to skeletons if the user navigates to a new
// category whose results haven't arrived yet.
const isInitialLoading = computed(
  () => pending.value && data.value.hits.length === 0,
)
</script>
